import { usePermissions, Entity, CrudAction } from 'utils/hooks/usePermissions'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { SettingsType } from '../../../utils/hooks/useSystemSettings'

type PermissionCheckResult = {
  hasPermission: boolean
  redirectPath: string | null
  showNoPermission: boolean
}

/**
 * Hook to check permissions for data pages and handle redirection
 * @param entity - The entity to check permission for
 * @param settingsKey - The settings key to check
 * @param fallbackPath - The path to redirect to if permission is denied (defaults to /data/flours)
 * @returns Object with permission status and redirection information
 */
export const useDataPermissions = (
  entity: Entity,
  settingsKey: SettingsType,
  fallbackPath: string = '/data/flours'
): PermissionCheckResult => {
  const { checkPermission } = usePermissions()
  const publicSettings = useSelector(
    (state: RootState) => state?.user?.settings || {}
  )

  const hasCurrentPermission =
    checkPermission(entity, CrudAction.GET) &&
    publicSettings[settingsKey] === true

  const hasFloursPermission =
    entity !== Entity.FLOURS &&
    checkPermission(Entity.FLOURS, CrudAction.GET) &&
    publicSettings['SHOW_FLOURS'] === true

  if (!hasCurrentPermission) {
    if (entity !== Entity.FLOURS && hasFloursPermission) {
      return {
        hasPermission: false,
        redirectPath: fallbackPath,
        showNoPermission: false,
      }
    }

    return {
      hasPermission: false,
      redirectPath: null,
      showNoPermission: true,
    }
  }

  return {
    hasPermission: true,
    redirectPath: null,
    showNoPermission: false,
  }
}
